import { createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { monotone, ticks } from './chart-math';

/** A line of the chart: its name, its colour (a CSS colour: `var(--chart-2)`), a value an hour. */
export interface Line {
  label: string;
  color: string;
  values: readonly number[];
}

const MARGIN = { top: 12, right: 12, bottom: 28, left: 40 };
const COMPACT = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
const HOUR = new Intl.DateTimeFormat('en', { hour: 'numeric' });
const DAY = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const TIME = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

/** logdash's `formatCount`: 1,234 under ten thousand, 12.5K over. */
const formatCount = (n: number) =>
  n < 10_000 ? n.toLocaleString('en', { maximumFractionDigits: 1 }) : COMPACT.format(n);
/** An hour on the x axis: `6pm`. */
const hourLabel = (t: number) => HOUR.format(t).replace(/\s/g, '').toLowerCase();
/** An hour in the tooltip: `Sat 10 Oct, 18:00`. */
const hourTitle = (t: number) => `${DAY.format(t)}, ${TIME.format(t)}`;

/**
 * logdash's `AnalyticsChart`, by the hour: monotone lines over dashed gridlines, the hour under the pointer (or the arrow
 * keys) marked and its values in a tooltip. Its size is its box's (`class`, 220 px high by default); the dots' ring is
 * the card's colour (`--chart-surface`).
 */
export function AnalyticsChart(props: {
  times: readonly number[];
  lines: readonly Line[];
  label: string;
  class?: string;
}) {
  const [width, setWidth] = createSignal(0);
  const [height, setHeight] = createSignal(0);
  const [hovered, setHovered] = createSignal<number | null>(null);
  let box!: HTMLDivElement;
  onMount(() => {
    const seen = new ResizeObserver(() => {
      setWidth(box.clientWidth);
      setHeight(box.clientHeight);
    });
    seen.observe(box);
    onCleanup(() => seen.disconnect());
  });

  const n = () => props.times.length;
  const yTicks = () => ticks(Math.max(0, ...props.lines.flatMap((l) => l.values)));
  const top = () => yTicks().at(-1) ?? 4;
  const x = (i: number) => MARGIN.left + (i / Math.max(1, n() - 1)) * Math.max(0, width() - MARGIN.right - MARGIN.left);
  const y = (v: number) => {
    const bottom = Math.max(MARGIN.top, height() - MARGIN.bottom);
    return bottom - (v / top()) * (bottom - MARGIN.top);
  };
  const labelIndexes = () => {
    const count = Math.max(2, Math.min(7, Math.floor(width() / 110)));
    if (n() <= count) return props.times.map((_, i) => i);
    const step = (n() - 1) / (count - 1);
    return Array.from({ length: count }, (_, i) => Math.round(i * step));
  };
  const clamp = (i: number) => Math.min(n() - 1, Math.max(0, i));

  const onPointerMove = (e: PointerEvent) => {
    const left = (e.currentTarget as SVGElement).getBoundingClientRect().left;
    const span = Math.max(1, width() - MARGIN.right - MARGIN.left);
    setHovered(clamp(Math.round(((e.clientX - left - MARGIN.left) / span) * (n() - 1))));
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = e.key === 'ArrowRight' ? 1 : -1;
    setHovered(clamp((hovered() ?? (step > 0 ? -1 : n())) + step));
  };

  return (
    <div ref={box} class={`relative w-full ${props.class ?? 'h-55'}`}>
      <Show when={width() && height()}>
        <svg
          width={width()}
          height={height()}
          class="focus-visible:outline-brand block touch-pan-y rounded-lg outline-none focus-visible:outline-2"
          role="img"
          tabindex="0"
          aria-label={`${props.label}. Use the arrow keys to read each hour.`}
          onPointerMove={onPointerMove}
          onPointerLeave={() => setHovered(null)}
          onKeyDown={onKeyDown}
          onBlur={() => setHovered(null)}
        >
          <For each={yTicks()}>
            {(tick) => (
              <>
                <line
                  x1={MARGIN.left}
                  x2={width() - MARGIN.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke="var(--surface-50-border)"
                  stroke-dasharray={tick === 0 ? undefined : '3 4'}
                />
                <text x={0} y={y(tick)} dy="0.32em" class="fill-fg-muted text-[11px] tabular-nums">
                  {formatCount(tick)}
                </text>
              </>
            )}
          </For>
          <For each={labelIndexes()}>
            {(i) => (
              <text
                x={x(i)}
                y={height() - 8}
                text-anchor={i === 0 ? 'start' : i === n() - 1 ? 'end' : 'middle'}
                class="fill-fg-muted text-[11px]"
              >
                {hourLabel(props.times[i] ?? 0)}
              </text>
            )}
          </For>
          <For each={props.lines}>
            {(line) => (
              <path
                d={monotone(line.values.map((v, i) => [x(i), y(v)] as const))}
                fill="none"
                stroke={line.color}
                stroke-width="2"
                stroke-linejoin="round"
              />
            )}
          </For>
          <Show when={hovered() !== null}>
            <line
              x1={x(hovered() ?? 0)}
              x2={x(hovered() ?? 0)}
              y1={MARGIN.top}
              y2={height() - MARGIN.bottom}
              stroke="var(--fg-disabled)"
            />
            <For each={props.lines}>
              {(line) => (
                <Show when={line.values[hovered() ?? 0] !== undefined}>
                  <circle
                    cx={x(hovered() ?? 0)}
                    cy={y(line.values[hovered() ?? 0] ?? 0)}
                    r="4"
                    fill={line.color}
                    stroke="var(--chart-surface, var(--surface-50-bg))"
                    stroke-width="2"
                  />
                </Show>
              )}
            </For>
          </Show>
        </svg>
      </Show>
      <Show when={hovered() !== null && props.times[hovered() ?? 0] !== undefined}>
        <div
          class={`border-surface-elevated-border bg-surface-elevated-bg pointer-events-none absolute top-2 z-10 min-w-44 rounded-xl border px-3 py-2.5 text-sm shadow-lg ${
            x(hovered() ?? 0) > width() / 2 ? '-translate-x-[calc(100%+12px)]' : 'translate-x-3'
          }`}
          style={{ left: `${x(hovered() ?? 0)}px` }}
        >
          <p class="text-fg-muted text-xs">{hourTitle(props.times[hovered() ?? 0] ?? 0)}</p>
          <For each={props.lines}>
            {(line) => (
              <Show when={line.values[hovered() ?? 0] !== undefined}>
                <p class="mt-1.5 flex items-center justify-between gap-4">
                  <span class="flex min-w-0 items-center gap-2">
                    <span class="size-2 shrink-0 rounded-full" style={{ 'background-color': line.color }} />
                    <span class="truncate">{line.label}</span>
                  </span>
                  <span class="font-medium tabular-nums">
                    {(line.values[hovered() ?? 0] ?? 0).toLocaleString('en')}
                  </span>
                </p>
              </Show>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
